/**
 * What a task walkthrough needs to know before step one (spec 33 5.5): what
 * this tab remembers, checked against the fleet, plus what the resolvers
 * find. A walkthrough that MINTS a value (Create a load's PO) gets a new one
 * unless it is resuming.
 *
 * On a practice fleet the PO is never minted or remembered: it is the
 * fleet's own (the lease's `po`), the one its stages continue and its
 * evidence checks, so "your load" is one load from Create a load onward
 * (training plan F7; resolvers.js `resolveContext`).
 *
 * TourHost (a card at a time) and TaskHost (spec 53 15, the task and its
 * checklist) both start a run from here, so the two read the same fleet the
 * same way.
 *
 * @param {object} options
 * @param {object} options.http the app's HTTP client
 * @param {object} options.definition a task definition
 * @param {boolean} options.resuming a run picked up again keeps what it minted
 * @param {object|null} options.lease the training lease, on a practice fleet
 */
import { readProgress, rememberContext } from './persistence';
import { resolveContext, loadIdForPo, freightLineFor, driverNameFor } from './resolvers';

export async function prepareContext({ http, definition, resuming, lease = null }) {
  const progress = readProgress();
  const needs = Array.isArray(definition.needs) ? definition.needs : [];
  const mints = Array.isArray(definition.mints) ? definition.mints : [];
  const fresh = resuming ? [] : mints;
  const fixed = lease && lease.po ? { po: lease.po } : {};
  const context = await resolveContext(http, needs, progress.context, {
    fresh,
    fixed,
    practice: Boolean(lease),
  });
  if (fresh.includes('po') && context.po) rememberContext({ po: context.po, loadId: null });
  // Spec 53: a step may be the practice fleet's alone (`requires:
  // ['training']`) or the demo's alone (`unless: ['training']`).
  if (lease) context.training = 'yes';

  // A load this tab made earlier: is it still there? (The visitor may have
  // deleted it; the tenant may have been reclaimed and replaced.)
  if (needs.includes('loadId') && !mints.includes('po')) {
    // Spec 52: a walkthrough about a SEEDED situation (the stuck sample
    // load) names `fallback.always`, and the visitor's own load never
    // replaces it: their load is not stuck, so the story would not hold.
    const always = Boolean(definition.fallback && definition.fallback.always);
    const id =
      context.po && !always
        ? await loadIdForPo(http, context.po, { newest: Boolean(fixed.po) })
        : null;
    if (id) {
      context.loadId = id;
      context.own = 'yes';
    } else if (definition.fallback && definition.fallback.po) {
      context.po = definition.fallback.po;
      const sample = await loadIdForPo(http, context.po);
      if (sample) context.loadId = sample;
      else delete context.po;
    } else {
      delete context.po;
      delete context.loadId;
    }
  }
  if (needs.includes('freightLine') && context.loadId) {
    const line = await freightLineFor(http, context.loadId);
    if (line) context.freightLine = line;
  }
  if (needs.includes('loadDriver') && context.loadId) {
    const driver = await driverNameFor(http, context.loadId);
    if (driver) context.loadDriver = driver;
  }
  return context;
}
